import { Timestamp } from '@weblite/ts-action'
import { RoomTypes } from '@weblite/ts-action/room'
import {
  CallType,
  CancelCall,
  JoinCall,
  RejectCall,
  reqCancelCall,
  reqJoinCall,
  reqRejectCall,
  reqStartCall,
  reqUnscheduleCall,
  reqUpdateCall,
  StartCall,
  UpdateCall,
} from '@weblite/ts-action/media-proxy'
import cloneDeep from 'lodash.clonedeep'
import { eventCallNameChanged } from '@weblite-wapps/protocol'
import hostConf from '$config/frontend'
import {
  showCallDeletedSnackbar,
  showCallEditedSnackbar,
  showErrorSnackbar,
} from '$comp/snackbars'
import {
  createNewInviteLink,
  getAllInviteLinks,
  getHall,
  isExpired,
  isPublicHall,
} from '$logic/hall'
import { getRoom, getRooms } from '$logic/room'
import { CallWappPanel } from '$logic/wapp-panels'
import { getClientUserId } from '$logic/client-user'
import { getHallPushData, getPvPushData, sendTextMessage } from '$logic/message'
import { closePvCallManager, openCall, openPvCallManager } from '$logic/router'
import { sendEventToWapp } from '$logic/wapp-communication/wapp-communication.publish'
import { request } from '$setup/ws'
import { t } from '$i18n'
import { sendEventToTwa } from '$logic/twaCommunication/twaCommunication.api'
import { TWA_EVENTS, twaCommunication } from '$logic/twaCommunication'
import { MAX_DATE } from '$helper/functions/time'
import { HallCallType } from '$comp/modals/create-hall-call/choose-call-time'
import { formatToTimeDay } from '$helper/functions/date'
import { showCallFeedbackDialog } from 'components/modals/call-feedback'
import { CallStorage, callStorage } from './storage'
import {
  endOngoingCallWithConfirmation,
  getCallId,
  isCallAtLeastOneMinuteOld,
  resetCallAndShowNPS,
} from './helpers'

const CALL_TYPE_TO_EVENT_MAPPER: Record<CallType, string> = {
  call: 'CHAT_START_CALL',
  group_call: 'CHAT_START_MEETING',
  live: 'CHAT_START_LIVE',
  tribune: 'CHAT_START_TRIBUNE',
}

async function createScheduledCallNotificationMessage(
  roomId: string,
  callType: string,
  callTime: Date,
  roomType: RoomTypes,
  name?: string,
) {
  const startsAt = formatToTimeDay(callTime)

  const inviteLinks = await getAllInviteLinks(roomId)
  const inviteLink =
    inviteLinks?.find(link => !link.isRevoked && !isExpired(link)) ||
    (await createNewInviteLink(roomId, +MAX_DATE, Infinity))
  const inviteLinkWithPrefix = inviteLink
    ? `${hostConf.FRONT_HOST}/+${inviteLink.link}`
    : ''

  return t('call.scheduledCallNotificationMessage')(
    t(`call.${callType}`),
    startsAt,
    t(`common.${roomType === 'CHANNEL' ? 'channel' : 'group'}`),
    name,
    inviteLinkWithPrefix,
  )
}

async function scheduleCall({
  roomId,
  roomType,
  callType,
  shouldSendNotification,
  callTime,
  name,
}: {
  roomId: string
  roomType: RoomTypes
  callType: HallCallType
  shouldSendNotification: boolean
  callTime: Date
  name?: string
}) {
  // TODO: Analytics
  const notificationMessage = shouldSendNotification
    ? await createScheduledCallNotificationMessage(
        roomId,
        callType,
        callTime,
        roomType,
        name,
      )
    : null

  const { error, payload } = await request<StartCall>(
    reqStartCall({
      roomId,
      roomType,
      callType,
      push: roomType === 'PV' ? getPvPushData() : await getHallPushData(roomId),
      startingAt: callTime,
      options: {
        name,
      },
    }),
  )

  if (error) {
    // TODO: Show error toast
  } else {
    const { error: resError } = payload
    if (resError) {
      if (resError === 'USER_BANNED')
        showErrorSnackbar(t('snackbar.call.userBanned')(t(`call.${callType}`)))
    }

    if (notificationMessage)
      sendTextMessage(notificationMessage, undefined, roomId, roomType)
  }
}

async function unscheduleCall(
  roomId: string,
  roomType: RoomTypes,
  callType: CallType,
) {
  const { error } = await request<CancelCall>(
    reqUnscheduleCall({
      roomId,
      roomType,
      callType,
    }),
  )
  if (error) {
    // TODO: show error toast
  } else {
    showCallDeletedSnackbar(t(`call.${callType}`))
  }
}

async function startCall(
  roomId: string,
  roomType: RoomTypes,
  callType: CallType,
  name?: string,
) {
  // if (isIOS) {
  //   try {
  //     await showIOSCallProblemsDialog()
  //   } catch (e) {
  //     return
  //   }
  // }

  const { roomId: currentCallRoomId, state } = callStorage.get()
  if (currentCallRoomId) await endOngoingCallWithConfirmation(roomId, callType)

  analite(CALL_TYPE_TO_EVENT_MAPPER[callType])

  if (callType === 'call') {
    if (currentCallRoomId && currentCallRoomId === roomId) {
      if (state === 'ongoing') openCall()
      else if (state === 'outgoing') openPvCallManager()

      if (state === 'ongoing' || state === 'outgoing') return
    }
    callStorage.reset()
    callStorage.set({
      roomId,
      state: 'outgoing',
      isCaller: true,
      callType: 'call',
    })
    openPvCallManager()
  }

  const { error, payload } = await request<StartCall>(
    reqStartCall({
      roomId,
      roomType,
      callType,
      push: roomType === 'PV' ? getPvPushData() : await getHallPushData(roomId),
      options: {
        name,
      },
    }),
  )

  callStorage.set({ hasNextCall: false })

  if (error) {
    // TODO: Show error toast
  } else if (callType !== 'call') {
    const { error: resError, token } = payload
    if (resError) {
      if (resError === 'USER_BANNED')
        showErrorSnackbar(t('snackbar.call.userBanned')(t(`call.${callType}`)))

      return
    }

    const { name } = await getHall(roomId)
    if (token)
      CallWappPanel.openCall(
        callType,
        token,
        callType === 'group_call'
          ? { title: name }
          : { title: name, channelId: roomId },
      )
    else return

    callStorage.reset()
    callStorage.set({
      roomId,
      callType,
      name,
      state: 'ongoing',
      isOwner: true,
      isParticipating: true,
      joinedAt: new Date().getTime(),
    })
  } else {
    // handling Notification for call in PV
    twaCommunication.showCallNotification(
      roomId,
      TWA_EVENTS.SHOW_ONGOING_CALL_NOTIFICATION,
    )
  }
}

async function joinCall(
  roomId: string,
  roomType: RoomTypes,
  callType: CallType,
) {
  // if (IS_IOS) {
  //   try {
  //     await showIOSCallProblemsDialog()
  //   } catch (e) {
  //     return
  //   }
  // }
  analite('JOIN_CALL', { callType })

  const { error, payload } = await request<JoinCall>(
    reqJoinCall({
      roomId,
      roomType,
      callType,
    }),
  )

  if (error)
    if (error.code === 'ROOM_FULL')
      showErrorSnackbar(t('snackbar.call.roomIsFull'))
    else showErrorSnackbar(t('snackbar.call.joinFailed')(t(`call.${callType}`)))
  else if (callType !== 'call') {
    const { error: resError, token } = payload
    if (resError) {
      if (resError === 'USER_BANNED')
        showErrorSnackbar(t('snackbar.call.userBanned')(t(`call.${callType}`)))

      return
    }

    const { name: hallName } = await getHall(roomId)
    const { call } = (await getRoom(roomId)) || {}
    const callName = call?.name

    if (token)
      CallWappPanel.openCall(callType, token, {
        title: callName || hallName,
        startedAt: call?.startedAt,
        ...(callType === 'group_call' ? undefined : { channelId: roomId }),
      })
    else return

    callStorage.reset()
    callStorage.set({
      roomId,
      callType,
      name: callName,
      state: 'ongoing',
      isOwner: false,
      isParticipating: true,
      startedAt: call?.startedAt,
      joinedAt: new Date().getTime(),
    })
    twaCommunication.showCallNotification(
      roomId,
      TWA_EVENTS.SHOW_ONGOING_CALL_NOTIFICATION,
      callType,
    )
  }
}

async function updateScheduledCall(
  roomId: string,
  roomType: RoomTypes,
  callType: CallType,
  shouldSendNotification: boolean,
  name: string | undefined,
  startedAt: Timestamp,
  newName?: string,
  newStartedAt?: Timestamp,
) {
  if (newStartedAt || newName) {
    const notificationMessage = shouldSendNotification
      ? await createScheduledCallNotificationMessage(
          roomId,
          callType,
          new Date(newStartedAt || startedAt),
          roomType,
          newName || name,
        )
      : null
    const { error } = await request<UpdateCall>(
      reqUpdateCall({
        roomId,
        roomType,
        callType,
        update: { name: newName, startedAt: newStartedAt },
      }),
    )
    if (error) {
      // TODO: show error toast
    } else {
      if (notificationMessage)
        sendTextMessage(notificationMessage, undefined, roomId, roomType)
      showCallEditedSnackbar(t(`call.${callType}`))
    }
  }
}

async function editCallName(name: string) {
  const { roomId, callType } = callStorage.get()
  if (!roomId || !callType) return

  const { type: roomType } = (await getRoom(roomId)) || {}
  if (!roomType) return
  const { error } = await request<UpdateCall>(
    reqUpdateCall({ roomId, roomType, callType, update: { name } }),
  )

  // TODO: handle error
  if (error) throw new Error()
}

async function sendNewNameToWapp(name: string) {
  if (!name) return

  callStorage.set({ name })
  const wappMeta = CallWappPanel.getWappMeta()

  if (wappMeta.wappSessionId)
    sendEventToWapp(eventCallNameChanged({ name }, wappMeta))
}

async function cancelOutgoingCall() {
  const { roomId } = callStorage.get()
  const { error } = await request<CancelCall>(
    reqCancelCall({
      roomId,
      roomType: 'PV',
      callType: 'call',
    }),
  )

  if (error) {
    // TODO: handle error
  } else {
    callStorage.reset()
    closePvCallManager()
  }
}

async function acceptIncomingCall() {
  // if (isIOS) {
  //   try {
  //     await showIOSCallProblemsDialog()
  //   } catch (e) {
  //     return
  //   }
  // }

  const { roomId } = callStorage.get()
  const { error } = await request<JoinCall>(
    reqJoinCall({
      roomId,
      roomType: 'PV',
      callType: 'call',
    }),
  )

  if (error) {
    // TODO: handle error
  } else {
    callStorage.set({
      roomId,
      callType: 'call',
      state: 'ongoing',
    })
    twaCommunication.showCallNotification(
      roomId,
      TWA_EVENTS.SHOW_ONGOING_CALL_NOTIFICATION,
    )
  }
}

async function rejectIncomingCall() {
  sendEventToTwa(TWA_EVENTS.REJECT_INCOMING_CALL)
  const { roomId } = callStorage.get()
  const { error } = await request<RejectCall>(
    reqRejectCall({
      roomId,
      roomType: 'PV',
      callType: 'call',
    }),
  )

  if (error) {
    // TODO: handle error
  } else {
    callStorage.reset()
    closePvCallManager()
  }
}

const NPSShownCalls = new Set<string>()

async function getFeedbackWithScore(call: CallStorage, identifier?: string) {
  if (!call?.callType) return
  const feedback = await showCallFeedbackDialog(call.callType)
  analite('SUBMIT_CALL_SCORE', {
    section: call.callType,
    roomId: call.roomId,
    identifier,
    score: feedback.rating,
    feedback: feedback.problem,
    feedbackDetails: feedback.problemDescription,
  })
}

/**
 *
 * @param call should be a deepClone of CallStorage
 * @returns
 */
export async function showCallNPS(call: CallStorage) {
  if (!call.callType || call.state !== 'ongoing') {
    return
  }
  const callId = getCallId(call)

  if (!callId || NPSShownCalls.has(callId) || !isCallAtLeastOneMinuteOld(call))
    return

  const isPublic = await isPublicHall(call.roomId)
  const identifier = isPublic ? call.roomId : undefined

  await getFeedbackWithScore(call, identifier)

  NPSShownCalls.add(callId)
}

async function endOngoingCall(
  callType: CallType,
  duration?: number,
  status?: CallStorage['status'],
) {
  sendEventToTwa(TWA_EVENTS.END_ONGOING_CALL)
  const call = callStorage.get()
  const { hasNextCall } = call
  const clonedCall = cloneDeep(call)

  if (hasNextCall) return

  if (callType !== 'live' && callType !== 'tribune')
    await CallWappPanel.closeCall(false)

  if (callType !== 'call') {
    callStorage.reset()
    showCallNPS(clonedCall)
    return
  }

  if (status === 'canceled') {
    callStorage.reset()
    closePvCallManager()
  } else {
    if (status === 'successful' || status === 'busy') openPvCallManager()
    callStorage.set({ duration, status, state: 'ended' })
  }
  showCallNPS(clonedCall)
}

async function checkForIncomingCalls() {
  const clientUserId = getClientUserId()
  const ringingPvs =
    getRooms().filter(
      ({ type, call }) =>
        type === 'PV' &&
        call?.type === 'call' &&
        clientUserId !== call.callerId &&
        !call.startedAt,
    ) || []

  const [ringingRoom] = ringingPvs
  if (ringingRoom) {
    callStorage.set({
      callType: 'call',
      roomId: ringingRoom.id,
      state: 'incoming',
      isCaller: false,
    })

    openPvCallManager()
  }
}

async function acceptIncomingCallByRoomId(roomId: string) {
  const { roomId: callRoomId } = callStorage.get() || {}
  if (callRoomId && callRoomId === roomId) await acceptIncomingCall()
}

async function endOngoingCallByRoomId(roomId: string) {
  const { roomId: callRoomId, callType } = callStorage.get() || {}
  if (callType && callRoomId && callRoomId === roomId)
    await endOngoingCall(callType)
}

async function rejectIncomingCallByRoomId(roomId: string) {
  const { roomId: callRoomId } = callStorage.get() || {}
  if (callRoomId && callRoomId === roomId) resetCallAndShowNPS()
}
export const callService = {
  startCall,
  joinCall,
  endOngoingCall,
  editCallName,
  sendNewNameToWapp,
  cancelOutgoingCall,
  acceptIncomingCall,
  rejectIncomingCall,
  checkForIncomingCalls,
  scheduleCall,
  unscheduleCall,
  updateScheduledCall,
  acceptIncomingCallByRoomId,
  endOngoingCallByRoomId,
  rejectIncomingCallByRoomId,
}
